import { useState, useEffect } from "react";
import Modal from "./Modal";
import { authApi } from "../../api/auth";
import useAuthStore from "../../store/authStore";

const PROVIDER_LABEL = { google: "Google", microsoft: "Microsoft" };

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-800">{value || "—"}</p>
    </div>
  );
}

function PasswordField({ label, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500 block mb-1">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full text-sm text-gray-900 bg-white border border-gray-300 rounded-lg px-3 py-2 pr-14 focus:outline-none focus:ring-2 focus:ring-bh-400"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600 text-xs"
          tabIndex={-1}
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
    </label>
  );
}

export default function UserSettingsModal({ open, onClose }) {
  const { user, updateUser } = useAuthStore();
  const isSSO = !!user?.oauth_provider;

  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState(null);
  const [success,         setSuccess]         = useState(false);

  useEffect(() => {
    if (open) {
      setNewPassword("");
      setConfirmPassword("");
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!newPassword) return setError("Enter a new password");
    if (newPassword !== confirmPassword) return setError("Passwords do not match");
    if (newPassword.length < 8) return setError("Password must be at least 8 characters");

    setSaving(true);
    try {
      const { data } = await authApi.updateMe({ new_password: newPassword });
      updateUser(data);
      setSuccess(true);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const initial = (user.full_name?.[0] || user.username?.[0] || "?").toUpperCase();
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" })
    : null;

  return (
    <Modal open={open} onClose={onClose} title="Account settings">
      <div className="space-y-5">

        {/* Avatar + identity */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-bh-500 flex items-center justify-center text-xl font-bold text-white shrink-0">
            {initial}
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900">
              {user.full_name || user.username}
            </p>
            <p className="text-sm text-gray-400">{user.email}</p>
            {isSSO && (
              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">
                {PROVIDER_LABEL[user.oauth_provider] ?? user.oauth_provider} SSO
              </span>
            )}
          </div>
        </div>

        {/* Read-only details */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
          <InfoRow label="Username"    value={user.username} />
          <InfoRow label="Entity"      value={user.entity_name ?? user.entity} />
          {memberSince && <InfoRow label="Member since" value={memberSince} />}
        </div>

        {/* Password change — hidden for SSO users */}
        {isSSO ? (
          <p className="text-sm text-gray-400">
            Password is managed by {PROVIDER_LABEL[user.oauth_provider] ?? user.oauth_provider}.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 pt-1 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">
              Change password
            </p>
            <div className="grid grid-cols-2 gap-3">
              <PasswordField
                label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="min. 8 characters"
              />
              <PasswordField
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {error   && <p className="text-xs text-red-600">{error}</p>}
            {success && <p className="text-xs text-green-600">Password updated.</p>}

            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50 transition-colors"
              >
                {saving ? "Saving…" : "Update password"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </form>
        )}

        {/* Close button for SSO users who have no form */}
        {isSSO && (
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        )}
      </div>
    </Modal>
  );
}
